import type { SamsaraVehicle, SamsaraDriver } from '@/types';
import { authFetch } from '@/utils/authFetch';

const SAMSARA_BASE = '/api/samsara';
const ENABLED = import.meta.env.VITE_ENABLE_SAMSARA === 'true';

async function samsaraFetch<T>(endpoint: string): Promise<T> {
  if (!ENABLED) {
    throw new Error('Samsara integration is not configured.');
  }

  const res = await authFetch(`${SAMSARA_BASE}${endpoint}`);

  if (!res.ok) {
    throw new Error(`Samsara API error: ${res.status} ${res.statusText}`);
  }

  return res.json();
}

/** Fetch all vehicles from Samsara fleet */
export async function getVehicles(): Promise<SamsaraVehicle[]> {
  if (!ENABLED) return getMockVehicles();

  const data = await samsaraFetch<{ data: SamsaraVehicle[] }>('/fleet/vehicles');
  return data.data;
}

/** Fetch a single vehicle by Samsara ID */
export async function getVehicle(samsaraId: string): Promise<SamsaraVehicle | null> {
  if (!ENABLED) return getMockVehicles().find((v) => v.id === samsaraId) ?? null;

  const data = await samsaraFetch<{ data: SamsaraVehicle }>(`/fleet/vehicles/${samsaraId}`);
  return data.data;
}

/** Fetch all drivers, mapping staticAssignedVehicle to vehicleId and vehicleName */
export async function getDrivers(): Promise<SamsaraDriver[]> {
  if (!ENABLED) return getMockDrivers();

  const data = await samsaraFetch<{ data: any[] }>('/fleet/drivers');

  return data.data.map((d: any) => ({
    id: d.id,
    name: d.name,
    email: d.email,
    phone: d.phone,
    username: d.username,
    vehicleId: d.staticAssignedVehicle?.id ?? d.vehicleId ?? undefined,
    vehicleName: d.staticAssignedVehicle?.name ?? undefined,
  }));
}

/** Search vehicle by unit number (label) */
export async function searchVehicleByUnit(unitNumber: string): Promise<SamsaraVehicle | null> {
  const vehicles = await getVehicles();
  return vehicles.find((v) => v.name.toLowerCase().includes(unitNumber.toLowerCase())) ?? null;
}

export async function getVehicleByQRCode(qrValue: string): Promise<SamsaraVehicle | null> {
  const vehicles = await getVehicles();
  return vehicles.find(v =>
    v.id === qrValue ||
    v.name.toLowerCase() === qrValue.toLowerCase() ||
    v.vin === qrValue
  ) ?? null;
}

// ============================================================
// Mock data — used when Samsara integration is disabled
// ============================================================

function getMockVehicles(): SamsaraVehicle[] {
  return [
    { id: 'sam-001', name: 'W3-101', vin: '1FTFW1ET0EKE12345', make: 'Ford', model: 'F-150', year: 2023, licensePlate: 'W3 1101', odometerMeters: 48280000 },
    { id: 'sam-002', name: 'W3-102', vin: '1GCGG25K571123456', make: 'Chevrolet', model: 'Express 2500', year: 2022, licensePlate: 'W3 1102', odometerMeters: 72420000 },
    { id: 'sam-003', name: 'W3-103', vin: '1FTBW2XM3HKA98765', make: 'Ford', model: 'Transit 250', year: 2024, licensePlate: 'W3 1103', odometerMeters: 16093000 },
    { id: 'sam-004', name: 'W3-201', vin: '3C6UR5DL9JG234567', make: 'RAM', model: 'ProMaster 2500', year: 2023, licensePlate: 'W3 2201', odometerMeters: 35405000 },
    { id: 'sam-005', name: 'W3-202', vin: '1GC4YPEY5MF345678', make: 'Chevrolet', model: 'Silverado 2500HD', year: 2023, licensePlate: 'W3 2202', odometerMeters: 56327000 },
  ];
}

function getMockDrivers(): SamsaraDriver[] {
  return [
    { id: 'drv-001', name: 'Marcus Johnson', email: 'mjohnson@wire3.com', vehicleId: 'sam-001', vehicleName: 'W3-101' },
    { id: 'drv-002', name: 'Sarah Chen', email: 'schen@wire3.com', vehicleId: 'sam-002', vehicleName: 'W3-102' },
    { id: 'drv-003', name: 'David Ramirez', email: 'dramirez@wire3.com', vehicleId: 'sam-003', vehicleName: 'W3-103' },
    { id: 'drv-004', name: 'Lisa Thompson', email: 'lthompson@wire3.com', vehicleId: 'sam-004', vehicleName: 'W3-201' },
  ];
}